/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { useCallback, useEffect, useState, useMemo } from 'preact/hooks';
import { events } from '@adobe-commerce/event-bus';
import { CartModel, Item } from '@/cart/data/models';
import { setGiftOptionsOnCart, updateProductsFromCart } from '@/cart/api';
import {
  DEFAULT_FORM_STATE,
  DEFAULT_CHECKBOXES_STATE,
  shouldShowGiftMessage,
  areGiftOptionsDisabled,
} from '@/cart/lib/giftOptionsHelper';
import {
  GiftOptionsDataSourcesProps,
  GiftOptionsViewProps,
  GiftWrappingConfigProps,
  GiftFormDataType,
  ProductGiftOptionsConfig,
} from '@/cart/types';
import { useText } from '@adobe-commerce/elsie/i18n';
import { state } from '@/cart/lib/state';

export interface UseGiftOptionsProps {
  item: Item | ProductGiftOptionsConfig;
  view: GiftOptionsViewProps;
  dataSource: GiftOptionsDataSourcesProps;
  initialLoading: boolean;
  handleItemsLoading?: (uid: string, state: boolean) => void;
  handleItemsError?: (uid: string, message?: string) => void;
  onItemUpdate?: ({ item }: { item: Item }) => void;
  onGiftOptionsChange?: (data: GiftFormDataType) => void;
}

export const useGiftOptions = ({
  item,
  view,
  dataSource,
  initialLoading,
  handleItemsLoading,
  handleItemsError,
  onItemUpdate,
  onGiftOptionsChange,
}: UseGiftOptionsProps) => {
  const isProductView = view === 'product';

  const dictionary = useText({
    requiredFieldError: 'Cart.GiftOptions.formText.requiredFieldError',
  });

  const [loading, setLoading] = useState(() => initialLoading);
  const [areGiftOptionsVisible, setAreGiftOptionsVisible] = useState({
    isGiftReceiptVisible: true,
    isPrintedCartVisible: true,
    isGiftWrappingVisible: true,
    isGiftOptionsVisible: true,
  });
  const [isGiftMessageVisible, setIsGiftMessageVisible] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [fieldsDisabled, setFieldsDisabled] = useState(false);
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [giftWrappingConfig, setGiftWrappingConfig] = useState<
    GiftWrappingConfigProps[] | []
  >([]);
  const [cartData, setCartData] = useState<CartModel | null>(null);
  const [errorsField, setErrorsField] =
    useState<typeof DEFAULT_FORM_STATE>(DEFAULT_FORM_STATE);
  const [giftOptions, setGiftOptions] = useState<GiftFormDataType>(() => ({
    giftWrappingId: '',
    ...DEFAULT_FORM_STATE,
    ...DEFAULT_CHECKBOXES_STATE,
  }));

  const isFormFilled =
    giftOptions.recipientName?.trim() &&
    giftOptions.senderName?.trim() &&
    giftOptions.message?.trim();

  const isFormEmpty =
    !giftOptions.recipientName?.trim() &&
    !giftOptions.senderName?.trim() &&
    !giftOptions.message?.trim();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const applyGiftOptionsToCart = useCallback(
    async (updatedGiftOptions: GiftFormDataType) => {
      // Update loading state used to render loading indicator required only in order view
      setUpdateLoading(view === 'order');
      setFieldsDisabled(true);

      switch (view) {
        case 'product':
          {
            if ('uid' in item) {
              handleItemsLoading?.(item.uid, true);
              handleItemsError?.(item.uid);
            }

            const {
              recipientName,
              senderName,
              message,
              giftWrappingId,
              isGiftWrappingSelected,
            } = updatedGiftOptions;

            const giftOptions = {
              gift_message: {
                to: recipientName ?? '',
                from: senderName ?? '',
                message: message ?? '',
              },
              // updateCartItems ignores `null` for gift_wrapping_id (treats it as
              // "unchanged"); the backend only clears it on an explicit empty string.
              gift_wrapping_id: isGiftWrappingSelected ? giftWrappingId : '',
            };

            if ('uid' in item && 'quantity' in item) {
              await updateProductsFromCart([
                {
                  uid: item.uid,
                  quantity: item.quantity,
                  giftOptions,
                },
              ])
                .then(() => {
                  onItemUpdate?.({ item });
                })
                .finally(() => {
                  handleItemsLoading?.(item.uid, false);
                  setFieldsDisabled(false);
                  setIsDataChanged(false);
                  setUpdateLoading(false);
                })
                .catch((error: any) => {
                  console.warn(error);
                });
            }
          }
          break;
        case 'order':
          await setGiftOptionsOnCart(updatedGiftOptions).finally(() => {
            setFieldsDisabled(false);
            setIsDataChanged(false);
            setUpdateLoading(false);
          });
          break;
        default:
          console.error(
            'Incorrect "view" prop value provided for GiftOptions container (storefront-cart)'
          );
          break;
      }
    },
    [handleItemsError, handleItemsLoading, item, onItemUpdate, view]
  );

  const updateGiftOptions = useCallback(
    (
      name: string,
      value: string | boolean | number | undefined,
      extraGiftOptions: Record<string, string | boolean | number> = {}
    ) => {
      setGiftOptions((prevGiftOptions: GiftFormDataType) => {
        const isValidGiftFormKey = (
          key: string
        ): key is keyof GiftFormDataType => key in prevGiftOptions;

        const getPrevValue = (key: string) =>
          isValidGiftFormKey(key) ? prevGiftOptions[key] : undefined;

        const hasChanged =
          getPrevValue(name) !== value ||
          Object.keys(extraGiftOptions).some(
            (key) => getPrevValue(key) !== extraGiftOptions[key]
          );

        if (!hasChanged) {
          onGiftOptionsChange?.(prevGiftOptions);
          return prevGiftOptions;
        }

        const updatedGiftOptions = {
          ...prevGiftOptions,
          [name]: value,
          ...extraGiftOptions,
        };

        if (
          !updatedGiftOptions.recipientName &&
          !updatedGiftOptions.senderName &&
          !updatedGiftOptions.message
        ) {
          setIsFormTouched(true);
        }

        if (
          typeof value === 'boolean' ||
          [
            'giftWrappingId',
            'giftReceiptIncluded',
            'printedCardIncluded',
          ].includes(name)
        ) {
          setIsFormTouched(false);
          typeof onGiftOptionsChange === 'function'
            ? onGiftOptionsChange(updatedGiftOptions)
            : applyGiftOptionsToCart(updatedGiftOptions);
        }

        setIsDataChanged(true);

        onGiftOptionsChange?.(updatedGiftOptions);
        return updatedGiftOptions;
      });
    },
    [applyGiftOptionsToCart, onGiftOptionsChange]
  );

  const handleFormMouseLeave = useCallback(
    async () => {
      // Fields disabled if other event handler already triggered
      if (fieldsDisabled) return;
      if (typeof onGiftOptionsChange === 'function') return;

      // Case when user removed all form fields - reset to initial state
      if (isFormEmpty && isFormTouched) {
        setErrorsField(DEFAULT_FORM_STATE);
        setIsFormTouched(false);
        await applyGiftOptionsToCart(giftOptions);
      }

      // Case when user edited one of form fields
      if (isDataChanged && isFormFilled) {
        setIsFormTouched(true);
        await applyGiftOptionsToCart(giftOptions);
      }
    },
    [
      applyGiftOptionsToCart,
      giftOptions,
      isDataChanged,
      isFormEmpty,
      isFormTouched,
      fieldsDisabled,
      onGiftOptionsChange,
      isFormFilled
    ]
  );

  const handleBlur = useCallback(
    async (event: Event) => {
      // Fields disabled if other event handler already triggered
      if (fieldsDisabled) return;
      if (typeof onGiftOptionsChange === 'function') return;

      const { name, value } = event.target as HTMLInputElement;

      setErrorsField((prevErrors: typeof DEFAULT_FORM_STATE) => ({
        ...prevErrors,
        [name]: value.trim() ? '' : dictionary.requiredFieldError,
      }));

      // Case when user removed all form fields - reset to initial state
      if (isFormEmpty && isFormTouched) {
        setErrorsField(DEFAULT_FORM_STATE);
        setIsFormTouched(false);
        await applyGiftOptionsToCart(giftOptions);
      }

      if (isDataChanged && isFormFilled) {
        await applyGiftOptionsToCart(giftOptions);
      }
    },
    [
      fieldsDisabled,
      isFormEmpty,
      isFormTouched,
      isDataChanged,
      isFormFilled,
      dictionary,
      applyGiftOptionsToCart,
      giftOptions,
      onGiftOptionsChange,
    ]
  );

  const onInputChange = useCallback(
    (event: Event) => {
      const target = event.target as HTMLInputElement;
      const name = target.name;
      const value = target.type === 'checkbox' ? target.checked : target.value;

      updateGiftOptions(name, value);
    },
    [updateGiftOptions]
  );

  useEffect(() => {
    if (isProductView) return;

    const dataEvent = events.on(
      // @ts-ignore
      dataSource === 'cart' ? 'cart/data' : 'order/data',
      (payload: CartModel) => {
        setCartData(payload);
        // Gift wrapping is allowed for the cart if at least one item has gift wrapping available
        const isCartGiftWrappingAllowed = payload?.items?.some(
          ({ giftWrappingAvailable }: { giftWrappingAvailable: boolean }) =>
            giftWrappingAvailable
        );
        const isCartGiftWrappingApplied = payload?.cartGiftWrapping?.some(
          (wrap: GiftWrappingConfigProps) => wrap.selected
        );

        // If gift wrapping is not allowed and it is applied, remove it
        if (!isCartGiftWrappingAllowed && isCartGiftWrappingApplied) {
          applyGiftOptionsToCart({
            ...giftOptions,
            giftWrappingId: '',
            isGiftWrappingSelected: false,
          });
        }
      },
      { eager: true }
    );
    return () => {
      dataEvent?.off();
    };
  }, [setCartData, applyGiftOptionsToCart, dataSource, giftOptions, isProductView]);

  const giftOptionsState: GiftFormDataType | null = useMemo(() => {
    if (!cartData && !item) return null;

    const giftWrappingOptions: GiftWrappingConfigProps[] = isProductView
      ? item?.productGiftWrapping?.map(
          (wrap: GiftWrappingConfigProps): GiftWrappingConfigProps => ({
            ...wrap,
            price:
              item?.giftWrappingPrice && item?.giftWrappingPrice?.value > 0
                ? item.giftWrappingPrice
                : wrap.price,
          })
        )
      : cartData?.cartGiftWrapping || [];

    const selectedWrap = giftWrappingOptions?.find((wrap) => wrap.selected);
    const selectedGiftWrappingId =
      selectedWrap?.uid ?? giftWrappingOptions?.[0]?.uid;

    const isGiftWrappingSelected = !!selectedWrap;

    const giftMessageConfig = isProductView
      ? item.giftMessage
      : cartData?.giftMessage;
    const printedCardIncluded = cartData?.printedCardIncluded;
    const giftReceiptIncluded = cartData?.giftReceiptIncluded;

    const giftMessageAvailable = shouldShowGiftMessage(item, isProductView);

    const allowGiftWrappingOnOrder = state.config?.allowGiftWrappingOnOrder;
    const allowGiftReceipt = state.config?.allowGiftReceipt;
    const allowPrintedCard = state.config?.allowPrintedCard;
    const isGiftWrappingAvailableForSome = cartData?.items?.some(
      (item: { giftWrappingAvailable: boolean }) => item.giftWrappingAvailable
    );
    const allowGiftWrappingOnProduct = item?.giftWrappingAvailable;
    const isGiftReceiptVisible = isProductView ? false : !!allowGiftReceipt;
    const isPrintedCartVisible = isProductView ? false : !!allowPrintedCard;
    const isGiftWrappingVisible = isProductView
      ? !!allowGiftWrappingOnProduct && !!giftWrappingOptions.length
      : !!allowGiftWrappingOnOrder &&
        !!giftWrappingOptions.length &&
        !!isGiftWrappingAvailableForSome;
    const checkBoxesIsHidden =
      !isGiftReceiptVisible && !isPrintedCartVisible && !isGiftWrappingVisible;

    setAreGiftOptionsVisible({
      isGiftReceiptVisible,
      isPrintedCartVisible,
      isGiftWrappingVisible,
      isGiftOptionsVisible: !checkBoxesIsHidden,
    });
    setIsGiftMessageVisible(giftMessageAvailable);

    return {
      ...(item && 'uid' in item ? { itemId: item.uid } : {}),
      ...(!isProductView
        ? {
            printedCardIncluded,
            giftReceiptIncluded,
          }
        : {}),
      ...giftMessageConfig,
      giftWrappingId: selectedGiftWrappingId,
      isGiftWrappingSelected,
      giftWrappingOptions,
    };
  }, [cartData, item, isProductView]);

  useEffect(() => {
    if (!giftOptionsState) return;

    const { giftWrappingOptions, ...giftOptionsFetchData } = giftOptionsState;

    setGiftOptions(giftOptionsFetchData);

    if (giftWrappingOptions?.length) {
      setGiftWrappingConfig(giftWrappingOptions);
    }

    setLoading(false);
  }, [giftOptionsState]);

  const isGiftOptionsApplied = useMemo(() => {
    return Object.entries(giftOptions)
      .filter(([key]) => key !== 'itemId' && key !== 'giftWrappingId')
      .some(([, value]) => Boolean(value));
  }, [giftOptions]);

  const isGiftOptionsHidden = useMemo(() => {
    return !loading && !!state?.config && areGiftOptionsDisabled(view, item);
  }, [item, loading, view]);

  return {
    loading,
    giftOptions,
    showModal,
    errorsField,
    updateLoading,
    cartData,
    fieldsDisabled,
    isGiftOptionsApplied,
    giftWrappingConfig,
    setFieldsDisabled,
    handleFormMouseLeave,
    onInputChange,
    updateGiftOptions,
    setShowModal,
    handleBlur,
    isGiftMessageVisible,
    areGiftOptionsVisible,
    isGiftOptionsHidden,
  };
};
